import { useState, useEffect, useCallback } from "react";
import { Table, Button, Input, Select, Space, message, Tooltip, Tag } from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  SearchOutlined,
  CheckOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import {
  listApprovalSummary,
  approveAllForRider,
  exportDocumentApproval,
} from "../../services/riderDocuments.service";
import DocumentDetailDrawer from "./DocumentDetailDrawer";
import { PartnerLabel } from "../../components/common/DeletedTag";
import StatusBadge from "../../components/common/StatusBadge";

const { Search } = Input;

const DOC_STATUS_PILLS = [
  { key: "all", label: "ALL" },
  { key: "pending", label: "PENDING" },
  { key: "approved", label: "APPROVED" },
  { key: "rejected", label: "REJECTED" },
];

const STATUS_COLORS = {
  pending: { color: "#c2410c", bg: "#fff7ed", border: "#fed7aa" },
  approved: { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  rejected: { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
  notSubmitted: { color: "#6b7280", bg: "#f3f4f6", border: "#e5e7eb" },
};

function DocStatusBadge({ status }) {
  const style = STATUS_COLORS[status] || STATUS_COLORS.notSubmitted;
  const label =
    status === "notSubmitted"
      ? "Not Submitted"
      : status
        ? status.toUpperCase()
        : "—";
  return (
    <Tag
      style={{
        color: style.color,
        background: style.bg,
        borderColor: style.border,
        fontWeight: 700,
        letterSpacing: 0.02,
      }}
    >
      {label}
    </Tag>
  );
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Document Approval tab — partner-centric list (Figma 5:620)
 */
export default function DocumentApprovalTab() {
  const { canApprove, canEdit } = usePermission("deliveryPartners");

  const { options: projectOptions, loading: projectsLoading } =
    useSettingsDropdown("projects");
  const { options: fleetOptions, loading: fleetsLoading } = useSettingsDropdown(
    "fleet-vehicle-types",
    { filter: (item) => item.category === "fleet" }
  );
  const { options: cityOptions, loading: citiesLoading } = useSettingsDropdown(
    "city-zones",
    { filter: (item) => item.entityType === "city" }
  );
  const { options: zoneOptions, loading: zonesLoading } = useSettingsDropdown(
    "city-zones",
    { filter: (item) => item.entityType === "zone" }
  );

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total_count: 0,
  });
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [docStatus, setDocStatus] = useState("all");
  const [drawer, setDrawer] = useState({ open: false, partner: null });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchList = useCallback(
    async (page = pagination.page) => {
      setLoading(true);
      try {
        const params = {
          page,
          per_page: pagination.per_page,
          search: search || undefined,
          docStatus,
          ...filters,
        };
        const response = await listApprovalSummary(params);
        setRows(response?.data?.records || []);
        setPagination((prev) => ({
          ...prev,
          page,
          total_count: response?.data?.pagination?.total_count || 0,
        }));
      } catch {
        message.error("Failed to load document approval list");
      } finally {
        setLoading(false);
      }
    },
    [pagination.page, pagination.per_page, search, filters, docStatus]
  );

  useEffect(() => {
    fetchList(1);
  }, [search, filters, docStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === undefined || value === null || value === "") {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const handleApproveAll = async (record) => {
    try {
      setActionLoading(true);
      const res = await approveAllForRider(record._id);
      message.success(res?.data?.msg || "Pending documents approved");
      fetchList(pagination.page);
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Approve failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const raw = await exportDocumentApproval({
        search: search || undefined,
        docStatus,
        ...filters,
      });
      const blob =
        raw instanceof Blob
          ? raw
          : new Blob([raw], { type: "text/csv;charset=utf-8" });
      downloadBlob(
        blob,
        `Document_Approval_${dayjs().format("YYYY-MM-DD")}.csv`
      );
      message.success("Download complete");
    } catch {
      message.error("Download failed");
    }
  };

  const columns = [
    {
      title: "PARTNER ID",
      dataIndex: "riderId",
      key: "riderId",
      width: 140,
      render: (v, record) => (
        <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
          <PartnerLabel name={v || "—"} deleted={record.riderDeleted || record.isDeleted} />
        </span>
      ),
    },
    {
      title: "PARTNER NAME",
      dataIndex: "fullName",
      key: "fullName",
      width: 220,
      render: (v, record) => (
        <strong>
          <PartnerLabel name={v} deleted={record.riderDeleted || record.isDeleted} />
        </strong>
      ),
    },
    {
      title: "WORK CONTACT",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      width: 140,
    },
    {
      title: "JOINED ON",
      dataIndex: "dateOfJoining",
      key: "dateOfJoining",
      width: 140,
      render: (d) => (d ? dayjs(d).format("MMM D, YYYY") : "—"),
    },
    {
      title: "PARTNER STATUS",
      dataIndex: "status",
      key: "partnerStatus",
      width: 120,
      render: (s) => <StatusBadge status={s} />,
    },
    {
      title: "STATUS",
      dataIndex: "documentApprovalStatus",
      key: "documentApprovalStatus",
      width: 140,
      render: (s) => <DocStatusBadge status={s} />,
    },
    {
      title: "ACTION",
      key: "action",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          {canApprove && record.documentApprovalStatus === "pending" && (
            <Tooltip title="Approve all pending">
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined style={{ color: "#15803d" }} />}
                loading={actionLoading}
                onClick={() => handleApproveAll(record)}
              />
            </Tooltip>
          )}
          {(canEdit || canApprove) && (
            <Tooltip title="Review documents">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined style={{ color: "#2563eb" }} />}
                onClick={() => setDrawer({ open: true, partner: record })}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="doc-approval-filters">
        <Space wrap size="middle" style={{ marginBottom: 16 }}>
          <Search
            placeholder="Search Partner ID / Name"
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={setSearch}
            style={{ width: 280 }}
          />
          <Select
            placeholder="Project"
            allowClear
            loading={projectsLoading}
            options={projectOptions}
            onChange={(v) => handleFilterChange("project", v)}
            style={{ width: 150 }}
          />
          <Select
            placeholder="Fleet Type"
            allowClear
            loading={fleetsLoading}
            options={fleetOptions}
            onChange={(v) => handleFilterChange("fleetType", v)}
            style={{ width: 140 }}
          />
          <Select
            placeholder="City"
            allowClear
            loading={citiesLoading}
            options={cityOptions}
            onChange={(v) => handleFilterChange("city", v)}
            style={{ width: 130 }}
          />
          <Select
            placeholder="Zones"
            allowClear
            loading={zonesLoading}
            options={zoneOptions}
            onChange={(v) => handleFilterChange("zone", v)}
            style={{ width: 130 }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => fetchList(pagination.page)} />
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Download Data
          </Button>
        </Space>

        <div className="doc-status-pills">
          {DOC_STATUS_PILLS.map((pill) => (
            <button
              key={pill.key}
              type="button"
              className={`doc-status-pill${docStatus === pill.key ? " active" : ""}`}
              onClick={() => setDocStatus(pill.key)}
            >
              {pill.label}
            </button>
          ))}
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey="_id"
        pagination={{
          current: pagination.page,
          pageSize: pagination.per_page,
          total: pagination.total_count,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} partners`,
          onChange: (page) => fetchList(page),
        }}
        scroll={{ x: 900 }}
      />

      <DocumentDetailDrawer
        open={drawer.open}
        partner={drawer.partner}
        onClose={() => setDrawer({ open: false, partner: null })}
        onChanged={() => fetchList(pagination.page)}
      />
    </div>
  );
}
